import * as React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScheduleFormData } from '@/lib/interviewTypes';

interface ScheduleInterviewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (formData: ScheduleFormData) => Promise<void>;
    batches: Array<{ _id: string; bCode: string; bTime?: string }>;
    isLoading: boolean;
}

const ScheduleInterviewDialog: React.FC<ScheduleInterviewDialogProps> = ({
    isOpen,
    onClose,
    onSubmit,
    batches,
    isLoading
}) => {
    const [formData, setFormData] = React.useState<ScheduleFormData>({
        dateTime: '',
        batchId: batches?.[0]?._id || '',
        moduleId: ''
    });

    React.useEffect(() => {
        if (isOpen) {
            setFormData({
                dateTime: '',
                batchId: batches?.[0]?._id || '',
                moduleId: ''
            });
        }
    }, [isOpen, batches]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSubmit(formData);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name: keyof ScheduleFormData, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-md dark:text-gray-100">
                <DialogHeader>
                    <DialogTitle>Schedule New Interview</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Date & Time */}
                    <div className="space-y-2">
                        <Label htmlFor="dateTime">Date & Time</Label>
                        <Input
                            id="dateTime"
                            type="datetime-local"
                            name="dateTime"
                            value={formData.dateTime}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    {/* Select Batch */}
                    <div className="space-y-2">
                        <Label htmlFor="batchId">Select Batch</Label>
                        <Select
                            value={formData.batchId}
                            onValueChange={(value) => handleSelectChange('batchId', value)}
                        >
                            <SelectTrigger id="batchId">
                                <SelectValue placeholder="-- Choose Batch --" />
                            </SelectTrigger>
                            <SelectContent>
                                {batches?.map((batch) => (
                                    <SelectItem key={batch._id} value={batch._id}>
                                        {batch.bCode} {batch.bTime ? `(${batch.bTime})` : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Select Module */}
                    <div className="space-y-2">
                        <Label htmlFor="moduleId">Interview Module</Label>
                        <Select
                            value={formData.moduleId}
                            onValueChange={(value) => handleSelectChange('moduleId', value)}
                        >
                            <SelectTrigger id="moduleId">
                                <SelectValue placeholder="-- Select Module --" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="0">HTML & CSS</SelectItem>
                                <SelectItem value="1">JavaScript</SelectItem>
                                <SelectItem value="2">Frontend</SelectItem>
                                <SelectItem value="3">Backend</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Action Buttons */}
                    <DialogFooter>
                        <Button
                            type="button"
                            onClick={onClose}
                            variant="outline"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="bg-[#22C55E] hover:bg-[#4ab872] text-white"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Scheduling...' : 'Schedule'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default ScheduleInterviewDialog;

